<template>
  <v-app-bar app color="surface" elevation="0" class="portal-header">
    <v-app-bar-nav-icon @click="ui.toggleDrawer()" />

    <v-toolbar-items class="ml-2 header-breadcrumbs">
      <v-breadcrumbs dense>
        <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
          <v-breadcrumbs-item :to="crumb.to" :disabled="idx === breadcrumbs.length - 1">
            {{ crumb.text }}
          </v-breadcrumbs-item>

          <!-- Insert a slash if this is not the last item -->
          <span v-if="idx < breadcrumbs.length - 1" class="mx-1" style="user-select: none">/</span>
        </template>
      </v-breadcrumbs>
    </v-toolbar-items>

    <v-toolbar-title class="ml-4"></v-toolbar-title>
    <v-spacer />
    <div id="header-page-actions" class="header-page-actions"></div>

    <!-- ─── TODAY’S DATE ──────────────────────────────────────────────────── -->
    <!-- Always visible; small font so it fits on mobile. -->
    <v-menu v-if="auth.isAuthenticated" location="bottom end">
      <template #activator="{ props }">
        <v-btn v-bind="props" class="signed-in-user" variant="text" :aria-label="`Signed in as ${userName}${userEmail ? ', ' + userEmail : ''}`">
          <v-icon icon="mdi-account-circle-outline" size="28" />
          <span class="user-label">
            <strong>{{ userName }}</strong>
            <span>{{ userEmail || 'Signed in' }}</span>
          </span>
          <v-icon icon="mdi-chevron-down" size="18" />
        </v-btn>
      </template>
      <v-card class="account-details" min-width="250" max-width="340">
        <v-card-text>
          <p class="account-caption">Signed in as</p>
          <strong>{{ userName }}</strong>
          <p v-if="userEmail">{{ userEmail }}</p>
        </v-card-text>
        <v-divider />
        <v-list density="compact">
          <v-list-item prepend-icon="mdi-logout" title="Logout" @click="onLogout" />
        </v-list>
      </v-card>
    </v-menu>
  </v-app-bar>
</template>

<script setup>
import { showConfirm } from '@/utils/messages';

import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { userManager } from "@/auth/oidc";
import { useTheme } from "vuetify";

const ui = useUIStore();
const auth = useAuthStore();
// Some identity providers return multiple name claims as an array.
const claimText = (value) => (Array.isArray(value) ? value.find(v => typeof v === 'string' && v.trim()) : typeof value === 'string' ? value : '')?.trim() || '';
const userEmail = computed(() => claimText(auth.user?.profile?.email));
const userName = computed(() => {
  const profile = auth.user?.profile || {};
  return [claimText(profile.given_name), claimText(profile.family_name)].filter(Boolean).join(' ')
    || claimText(profile.name) || userEmail.value || claimText(profile.preferred_username) || 'Signed-in user';
});
const route = useRoute();

const breadcrumbs = computed(() => {
  return route.matched
    .filter((r) => r.meta?.breadcrumb)
    .map((r) => {
      // Special links for parent routes
      if (r.name === "ZoneParent") {
        return { text: r.meta.breadcrumb, to: { name: "ZoneList" } };
      }
      if (r.name === "FormParent") {
        return { text: r.meta.breadcrumb, to: { name: "FormList" } };
      }

      // Identify which params are valid for this route
      const validParams = Object.keys(route.params).filter((key) => r.path.includes(`:${key}`));

      const params = validParams.length
        ? Object.fromEntries(validParams.map((k) => [k, route.params[k]]))
        : undefined;

      return {
        text: r.meta.breadcrumb,
        to: r.name ? { name: r.name, params } : { path: r.path },
      };
    });
});

// ─── TODAY’S DATE FORMATTED ───────────────────────────────────────────────
const todayString = computed(() => {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
});

// ─── USER PROFILE ACTIONS ──────────────────────────────────────────────
async function onLogout() {
  if ((await showConfirm("Are you sure you want to logout?"))) {
    try {
      // 1. Call your auth store logout (handles token cleanup, etc.)
      await auth.logout();

      // 2. The auth store should handle the redirect
      // If not, add: router.push('/login')
    } catch (error) {
      console.error("Logout error:", error);

      // Fallback: Force logout
      auth.clearUser();
      localStorage.clear(); // Clear any remaining tokens
      window.location.href = "/login";
    }
  }
}
</script>

<style scoped>
.header-page-actions { display: flex; align-items: center; margin-right: 8px; }
.signed-in-user { flex-shrink: 0; margin-right: 12px; height: 48px !important; text-transform: none; letter-spacing: normal; }
.user-label { display: flex; flex-direction: column; align-items: flex-start; margin: 0 10px; max-width: 230px; line-height: 1.4; }
.user-label strong, .user-label > span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-label strong { font-size: 13px; color: var(--ww-navy); }
.user-label > span, .account-caption { font-size: 12px; color: var(--ww-text-muted); }
.account-details { overflow-wrap: anywhere; }
.account-details p { margin: 4px 0; }
@media (max-width: 1000px) { .header-breadcrumbs { display: none; } .user-label { max-width: 160px; } }
@media (max-width: 600px) { .user-label { display: none; } .signed-in-user { min-width: 44px; margin-right: 4px; padding: 0 6px; } }
.v-breadcrumbs {
  font-size: 0.875rem; /* ~14px so it fits nicely in the app-bar */
  margin-top: 0;
  margin-bottom: 0;
}

.text-caption {
  font-size: 0.85rem !important;
}
</style>
